import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {UnmountClosed} from 'react-collapse';
import dayjs from 'dayjs';
import {AnimatePresence} from 'framer-motion';
import {StyledOverviewUserPageContent, StyledOverviewUserPageSpin, StyledOverviewUserPageHeaderSpace} from './StyledOverviewUserPage';
import {StyledOverviewPageContainer, StyledOverviewPageContent} from '../OverviewPage/StyledOverviewPage';
import DashboardPageTransition from '../../components/animations/DashboardPageTransition';
import RightSidebar from '../../components/animations/RightSidebar';
import BalancePageHeader from '../../components/pages/TransactionsPage/BalancePageHeader';
import PageDocumentDetails from '../../components/PageDocumentDetails';
import CompleteKycCard from '../../components/pages/OverviewPage/CompleteKycCard';
import Last30DaysSpending from '../../components/pages/OverviewPage/Last30DaysSpending';
import TotalCardsBalance from '../../components/pages/OverviewPage/TotalCardsBalance';
import UserOverviewRightPanel from '../../components/pages/OverviewPage/UserOverviewRightPanel';
import Expenses from '../../components/pages/OverviewPage/Expenses';
import {useIsBankingEnabled, useIsCompanyChanged, useIsRequiredCardVerification} from '../../hooks';
import {cardActions} from '../../state/actions/card.actions';
import {overviewActions} from '../../state/actions/overview.actions';
import {overviewPageUtils} from '../OverviewPage/utils';
import {cardsHelpers, dateHelpers} from '../../utils/helpers';

const {month, year} = dateHelpers.getCurrentDate();

const defaultSpendingData = {
  loaded: false,
  query: { month, year },
  spending: [],
}

const OverviewUserPage = ({
  employeeEmail,
  navigationLoading,
  getOverview,
  getUserCards,
}) => {
  const [t] = useTranslation('main');
  const [actualSpendingData, setActualSpendingData] = useState(defaultSpendingData);
  const [expectedSpendingData, setExpectedSpendingData] = useState(defaultSpendingData);
  const [activeCard, setActiveCard] = useState(null);
  const [cards, setCards] = useState([]);
  const [isOpenDetails, setIsOpenDetails] = useState(false);

  const { required: isRequiredCardVerification } = useIsRequiredCardVerification();

  const companyId = useIsCompanyChanged();

  const isBankingEnabled = useIsBankingEnabled();

  const last30DaysSpending = useMemo(() => {
    const data = overviewPageUtils.calculateSpendingLast30Days(actualSpendingData?.spending, expectedSpendingData?.spending);
    return {
      amount: data?.actual,
      percentage: overviewPageUtils.calculateSpendingPercentage(data.actual, data.expected),
    }
  }, [actualSpendingData?.spending, expectedSpendingData?.spending]);

  const totalCardsBalance = useMemo(() => {
    const calculateSum = (key) => cards.reduce((sum, card) => sum + (Number(card[key]) || 0), 0);
    return cards.length ? { actual: calculateSum('amount'), expected: calculateSum('custom_limit') } : { actual: 0, expected: 0 };
  }, [cards]);

  useEffect(() => {
    if (employeeEmail) {
      loadOverview(employeeEmail);
    }
  }, [employeeEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (companyId && isBankingEnabled) {
      getUserCards({
        query: { company_id: companyId },
        successCallback: (data) => {
          const cards = data?.results || [];
          const activeCard = cards.find(cardsHelpers.checkIsActivePhysicalCard);
          setCards(cards);
          setActiveCard(activeCard || null);
        }
      });
    }
  }, [companyId, isBankingEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const getOverviewData = ({query, setState}) => {
    getOverview(
      query,
      (data) => {
        setState(prevState => ({
          ...prevState,
          spending: overviewPageUtils.getUserSpending({data, employeeEmail, query}),
          loaded: true,
        }));
      },
      () => setState(prevState => ({...prevState, loaded: true}))
    );
  }

  const loadOverview = () => {
    const query = actualSpendingData?.query;
    // mark that data is not loaded yet
    setActualSpendingData({...actualSpendingData, loaded: false});
    setExpectedSpendingData({...expectedSpendingData, loaded: false});

    // check if previous month is current month - 60 days, if yes, then we don't need to load 3 months, just 2
    const today = dayjs();
    const sixtyDaysAgo = today.subtract(59, 'day'); // inclusive
    // previous month
    const previousMonth = dayjs().subtract(1, 'month').month() + 1;
    const sixtyDaysAgoMonth = sixtyDaysAgo.month() + 1;
    const isNeedThreeMonths = previousMonth !== sixtyDaysAgoMonth;
    const previousMonthQuery = overviewPageUtils.adjustPreviousQueryForMonthly(query);

    getOverviewData({employeeEmail, query, setState: setActualSpendingData});

    if (!isNeedThreeMonths) {
      getOverviewData({
        employeeEmail,
        query: previousMonthQuery,
        setState: setExpectedSpendingData
      });
    } else {
      const threeMonthsQuery = overviewPageUtils.adjustPreviousQueryForMonthly(previousMonthQuery);
      getOverview(
        previousMonthQuery,
        (data) => {
          getOverview(
            threeMonthsQuery,
            (data2) => {
              setExpectedSpendingData({
                loaded: true,
                spending: [
                  ...overviewPageUtils.getUserSpending({data, employeeEmail, query: previousMonthQuery}),
                  ...overviewPageUtils.getUserSpending({data: data2, employeeEmail, query: threeMonthsQuery})
                ]
              });
            },
            () => setExpectedSpendingData({...expectedSpendingData, loaded: true})
          )
        },
        () => setExpectedSpendingData({...expectedSpendingData, loaded: true})
      );
    }
  }

  const handleDeleteCard = () => {
    const activeCardId = activeCard?.card_id;
    const nextActiveCard = cards.find(card => cardsHelpers.checkIsActivePhysicalCard(card) && card.card_id !== activeCardId);
    setActiveCard(nextActiveCard || null);
  }

  return (
    <StyledOverviewPageContainer>
      <PageDocumentDetails title={t('pageTitles.overview')} />
      <BalancePageHeader  breadcrumbs={[{title: t('overview')}]} />
      <DashboardPageTransition>
        <StyledOverviewPageContent
          align='start'
          className='full-details-open'
          size='large'
        >
          <StyledOverviewUserPageSpin
            className='w-100'
            size='small'
            spinning={navigationLoading}
          >
            <StyledOverviewUserPageContent
              align='start'
              direction='vertical'
              size='large'
            >
              <UnmountClosed isOpened={isRequiredCardVerification}>
                <CompleteKycCard variant='banking' />
              </UnmountClosed>
              <StyledOverviewUserPageHeaderSpace className={isBankingEnabled ? '' : 'single-item'}>
                <Last30DaysSpending {...last30DaysSpending} />
                {isBankingEnabled && <TotalCardsBalance {...totalCardsBalance} />}
              </StyledOverviewUserPageHeaderSpace>
              <Expenses handleOpenDetails={setIsOpenDetails} />
            </StyledOverviewUserPageContent>
          </StyledOverviewUserPageSpin>
          <AnimatePresence>
            <RightSidebar className='w-100' animate={isOpenDetails ? 'hidden' : 'visible'}>
              <UserOverviewRightPanel card={activeCard} onDeleteCard={handleDeleteCard} />
            </RightSidebar>
          </AnimatePresence>
        </StyledOverviewPageContent>

      </DashboardPageTransition>
    </StyledOverviewPageContainer>
  );
}

const mapStateToProps = state => {
  const {main} = state;
  const navigationLoading = main?.navigationLoading || false;
  const {email: employeeEmail} = state.user.employee;

  return {
    employeeEmail,
    navigationLoading,
  }
}

const mapDispatchToProps = {
  getOverview: overviewActions.getOverview,
  getUserCards: cardActions.getUserCards,
}

export default connect(mapStateToProps, mapDispatchToProps)(OverviewUserPage);
